import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Calculator } from 'lucide-react'
import {
  amazonModelPreview,
  type AmazonFeeOverride,
  type AmazonModelAssumptions,
  type AmazonModelPreviewRow,
  type AmazonModelReason,
} from '@/api/pricing'
import { ApiError } from '@/api/client'
import { Panel } from '@/components/Panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { fmtEUR } from '@/lib/pricing-format'

const DEFAULT_MODEL: AmazonModelAssumptions = {
  vat_pct: 19,
  default_fee_pct: 13,
  advertising_pct: 15,
  risk_pct: 3,
  target_margin_pct: 25,
  legacy_all_in_pct: 46,
}

const FIELDS: { key: keyof AmazonModelAssumptions; label: string }[] = [
  { key: 'vat_pct', label: 'USt. %' },
  { key: 'default_fee_pct', label: 'Amazon-Gebühr Standard %' },
  { key: 'advertising_pct', label: 'Werbung %' },
  { key: 'risk_pct', label: 'Risiko %' },
  { key: 'target_margin_pct', label: 'Zielmarge %' },
  { key: 'legacy_all_in_pct', label: 'Bisheriger All-in-Puffer %' },
]

const REASONS: Record<AmazonModelReason, string> = {
  unknown_ek: 'EK unbekannt',
  invalid_cost: 'Kostenbasis ungültig',
  invalid_assumptions: 'Annahmen ungültig',
  legacy_infeasible: 'Bisheriges Modell rechnerisch unmöglich',
  detailed_infeasible: 'Detailmodell rechnerisch unmöglich',
}

function parseNumber(value: string, label: string): number {
  const normalized = value.trim().replace(',', '.')
  if (!normalized) throw new Error(`${label}: Wert fehlt`)
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed) || parsed < 0 || parsed >= 100) {
    throw new Error(`${label}: Wert muss zwischen 0 und kleiner 100 liegen`)
  }
  return parsed
}

/** Eine Zeile je manuell übernommener Messquote: Seller-SKU=12,83 */
export function parseFeeOverrides(raw: string): AmazonFeeOverride[] {
  const result: AmazonFeeOverride[] = []
  const seen = new Set<string>()
  for (const [index, rawLine] of raw.split(/\r?\n/).entries()) {
    const line = rawLine.trim()
    if (!line) continue
    const separator = line.search(/[=;\t]/)
    if (separator <= 0) throw new Error(`Gebührenzeile ${index + 1}: erwartet Seller-SKU=Prozent`)
    const sellerSku = line.slice(0, separator).trim()
    const feeRaw = line.slice(separator + 1).trim()
    if (!sellerSku || seen.has(sellerSku)) {
      throw new Error(`Gebührenzeile ${index + 1}: Seller-SKU fehlt oder ist doppelt`)
    }
    const feePct = parseNumber(feeRaw, `Gebührenzeile ${index + 1}`)
    if (feePct <= 0) throw new Error(`Gebührenzeile ${index + 1}: Gebühr muss größer 0 sein`)
    seen.add(sellerSku)
    result.push({ seller_sku: sellerSku, fee_pct: feePct })
  }
  return result
}

function scenarioPrice(row: AmazonModelPreviewRow, which: 'legacy' | 'detailed') {
  const scenario = row.comparison[which]
  if (scenario.price !== null) return fmtEUR(scenario.price)
  return scenario.reason ? REASONS[scenario.reason] : 'nicht berechenbar'
}

export function AmazonModelComparison({ selectedSkus }: { selectedSkus: string[] }) {
  const [values, setValues] = useState<Record<keyof AmazonModelAssumptions, string>>(
    Object.fromEntries(
      Object.entries(DEFAULT_MODEL).map(([key, value]) => [key, String(value)]),
    ) as Record<keyof AmazonModelAssumptions, string>,
  )
  const [overrides, setOverrides] = useState('')
  const [rows, setRows] = useState<AmazonModelPreviewRow[]>([])
  const [unusedOverrides, setUnusedOverrides] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const revisionRef = useRef(0)
  const selectionKey = selectedSkus.join('\u001f')

  function invalidateResult() {
    revisionRef.current += 1
    setRows([])
    setUnusedOverrides([])
    setError(null)
  }

  useEffect(() => {
    invalidateResult()
  }, [selectionKey])

  const preview = useMutation({
    mutationFn: ({
      request,
    }: {
      request: Parameters<typeof amazonModelPreview>[0]
      revision: number
    }) => amazonModelPreview(request),
    onSuccess: (result, variables) => {
      if (variables.revision !== revisionRef.current) return
      setRows(result.rows)
      setUnusedOverrides(result.unused_fee_overrides)
      setError(null)
    },
    onError: (err, variables) => {
      if (variables.revision !== revisionRef.current) return
      setError(err instanceof ApiError ? err.message : 'Modellvergleich fehlgeschlagen')
    },
  })

  function runPreview() {
    try {
      const model = Object.fromEntries(
        FIELDS.map(({ key, label }) => [key, parseNumber(values[key], label)]),
      ) as unknown as AmazonModelAssumptions
      const feeOverrides = parseFeeOverrides(overrides)
      setError(null)
      preview.mutate({
        request: { skus: selectedSkus, model, fee_overrides: feeOverrides },
        revision: revisionRef.current,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ungültige Modelleingabe')
    }
  }

  return (
    <Panel
      title="Amazon-Modellvergleich"
      subtitle="Nur Vorschau – speichert keine Entwürfe und startet keinen Preisrollout."
      actions={
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={selectedSkus.length === 0 || preview.isPending}
          onClick={runPreview}
        >
          <Calculator className="size-3.5" />
          Modell vergleichen ({selectedSkus.length})
        </Button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {FIELDS.map(({ key, label }) => (
          <label key={key} className="text-xs text-ink-2">
            {label}
            <Input
              className="mt-1 font-mono"
              type="number"
              min="0"
              max="99.9999"
              step="0.01"
              aria-label={label}
              value={values[key]}
              onChange={(event) => {
                invalidateResult()
                setValues((current) => ({ ...current, [key]: event.target.value }))
              }}
            />
          </label>
        ))}
      </div>

      <label className="block text-xs text-ink-2 mt-4">
        Optionale artikelbezogene Messquoten
        <Textarea
          className="mt-1 min-h-20 font-mono text-xs"
          aria-label="Artikelbezogene Amazon-Gebührenquoten"
          placeholder={'Seller-SKU=Prozent, eine Zeile je Artikel\n60-08-115N=12,83'}
          value={overrides}
          onChange={(event) => {
            invalidateResult()
            setOverrides(event.target.value)
          }}
        />
        <span className="block mt-1 text-muted">
          Manuelle Eingabe; es werden keine Product-Fees-Daten automatisch geladen oder gespeichert.
        </span>
      </label>

      {error && (
        <p role="alert" className="mt-3 text-sm text-coral">
          {error}
        </p>
      )}
      {unusedOverrides.length > 0 && (
        <p role="alert" className="mt-3 text-sm text-warn">
          Nicht zugeordnete Seller-SKU: {unusedOverrides.join(', ')}. Für diese Eingaben wurde kein
          Artikelwert verwendet.
        </p>
      )}

      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line-2 text-xs uppercase text-muted">
                <th className="text-left font-medium px-3 py-2">Artikel</th>
                <th className="text-right font-medium px-2 py-2">Amazon aktuell</th>
                <th className="text-right font-medium px-2 py-2">EK + Versand</th>
                <th className="text-right font-medium px-2 py-2">Gebühr</th>
                <th className="text-right font-medium px-2 py-2">DB vor Werbung</th>
                <th className="text-right font-medium px-2 py-2">Bisheriges Modell</th>
                <th className="text-right font-medium px-3 py-2">Detailmodell</th>
                <th className="text-right font-medium px-3 py-2">Differenz</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.sku} className="border-b border-line-2 last:border-0">
                  <td className="px-3 py-2">
                    <div className="text-ink">{row.name}</div>
                    <div className="font-mono text-[11px] text-muted">
                      {row.seller_sku ?? row.sku}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right font-mono">
                    {row.current_amazon_price === null ? '—' : fmtEUR(row.current_amazon_price)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono">
                    {row.comparison.cost_base === null ? '—' : fmtEUR(row.comparison.cost_base)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono">
                    {row.fee_pct.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 4,
                    })}{' '}
                    %
                    <div className="text-[10px] text-muted">
                      {row.fee_source === 'sku_override' ? 'Artikelwert' : 'Standard'}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right font-mono">
                    {row.comparison.current_db_before_advertising === null
                      ? '—'
                      : fmtEUR(row.comparison.current_db_before_advertising)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{scenarioPrice(row, 'legacy')}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {scenarioPrice(row, 'detailed')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {row.comparison.detailed_minus_legacy === null
                      ? '—'
                      : `${row.comparison.detailed_minus_legacy >= 0 ? '+' : '−'}${fmtEUR(Math.abs(row.comparison.detailed_minus_legacy))}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}
